import Phaser from 'phaser';
import { COLORS } from './palette';

export type BackdropOpts = {
  /** If true, also draws subtle scanlines over the top. */
  scanlines?: boolean;
  /** If true, draws drifting ambient dots in the background. */
  particles?: boolean;
  /** Strength of the central glow (0..1). */
  glow?: number;
  /** Tint applied to the central glow. Defaults to COLORS.bgGlow. */
  glowTint?: number;
  /** Tint for the soft spread layer below the glow. */
  spreadTint?: number;
};

/**
 * Adds a multi-layer background to a scene:
 *   - solid base color
 *   - radial-ish central glow (additive)
 *   - perspective grid floor
 *   - drifting ambient particles
 *   - vignette + scanlines on top
 *
 * Returns an object you can destroy() if needed.
 */
export class Backdrop {
  private scene: Phaser.Scene;
  private layers: Phaser.GameObjects.GameObject[] = [];
  private gridT = 0;
  private rippleHost?: Phaser.GameObjects.Graphics;
  private scanlines?: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, opts: BackdropOpts = {}) {
    this.scene = scene;
    const { width, height } = scene.scale;

    scene.cameras.main.setBackgroundColor(COLORS.bg);

    // central glow
    const glowAlpha = opts.glow ?? 0.55;
    const glow = scene.add
      .circle(width / 2, height * 0.42, Math.max(width, height) * 0.55, opts.glowTint ?? COLORS.bgGlow, glowAlpha)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(-100);
    this.layers.push(glow);

    // soft tinted edges (additive blue spread)
    const spread = scene.add
      .circle(width / 2, height * 0.6, Math.max(width, height) * 0.9, opts.spreadTint ?? 0x0a1430, 0.4)
      .setDepth(-99);
    this.layers.push(spread);

    // perspective grid
    const grid = scene.add.graphics().setDepth(-90);
    this.layers.push(grid);
    const drawGrid = () => {
      grid.clear();
      const horizon = height * 0.4;
      const bottom = height + 40;

      // horizontal lines receding into horizon
      const rows = 14;
      for (let i = 0; i < rows; i++) {
        const t = i / (rows - 1);
        // distribute lines with perspective spacing (more density near horizon)
        const y = horizon + (bottom - horizon) * (t * t);
        const a = 0.05 + (1 - t) * 0.18;
        grid.lineStyle(1, COLORS.bgGridNear, a);
        grid.lineBetween(0, y, width, y);
      }

      // vanishing-point verticals
      const cx = width / 2;
      const cols = 13;
      for (let i = 0; i < cols; i++) {
        const t = (i - (cols - 1) / 2) / ((cols - 1) / 2);
        const xBottom = cx + t * (width * 1.4);
        grid.lineStyle(1, COLORS.bgGridFar, 0.18);
        grid.lineBetween(cx, horizon, xBottom, bottom);
      }

      // a brighter sweep line drifting down for motion
      const sweep = (this.gridT % 1);
      const yS = horizon + (bottom - horizon) * (sweep * sweep);
      grid.lineStyle(1.5, 0x4a6cff, 0.35 * (1 - sweep));
      grid.lineBetween(0, yS, width, yS);
    };
    drawGrid();
    let lastGridT = -1;
    scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 4200,
      repeat: -1,
      onUpdate: (tw) => {
        const v = tw.getValue() ?? 0;
        // grid redraw is expensive (many lines) — throttle to ~20fps
        if (Math.abs(v - lastGridT) < 0.018) return;
        lastGridT = v;
        this.gridT = v;
        drawGrid();
      },
    });

    // ambient drifting particles (count tuned for 1080×1920)
    if (opts.particles !== false) {
      for (let i = 0; i < 18; i++) {
        const x = Math.random() * width;
        const y = Math.random() * height;
        const r = Math.random() * 1.6 + 0.6;
        const dot = scene.add
          .circle(x, y, r, 0x6a8cff, 0.35 + Math.random() * 0.35)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setDepth(-80);
        this.layers.push(dot);
        scene.tweens.add({
          targets: dot,
          y: y - (40 + Math.random() * 60),
          alpha: { from: dot.alpha, to: 0 },
          duration: 6000 + Math.random() * 4000,
          repeat: -1,
          yoyo: false,
          onRepeat: () => {
            dot.x = Math.random() * width;
            dot.y = height + 10;
            dot.setAlpha(0.4 + Math.random() * 0.35);
          },
        });
      }
    }

    // vignette using radial-ish stack of large hollow circles
    // sits above the game content but below HUD (HUD uses depth 900+)
    const vignette = scene.add.graphics().setDepth(200);
    this.layers.push(vignette);
    for (let i = 0; i < 8; i++) {
      const a = 0.05 + i * 0.022;
      const r = Math.max(width, height) * (0.55 + i * 0.06);
      vignette.lineStyle(80, COLORS.vignette, a);
      vignette.strokeCircle(width / 2, height / 2, r);
    }

    // scanlines
    if (opts.scanlines !== false) {
      const lines = scene.add.graphics().setDepth(210);
      this.layers.push(lines);
      lines.lineStyle(1, 0xffffff, 0.022);
      for (let y = 0; y < height; y += 3) {
        lines.lineBetween(0, y, width, y);
      }
      this.scanlines = lines;
    }

    // ripple host — for big-event radiating rings
    this.rippleHost = scene.add.graphics().setDepth(-85).setBlendMode(Phaser.BlendModes.ADD);
    this.layers.push(this.rippleHost);
  }

  /**
   * Spawn a radiating ring at (x, y). Used for tap-impact + collapse echoes.
   * `tint` is a 0xRRGGBB color; `power` (0..1) scales radius and brightness.
   */
  ripple(x: number, y: number, tint: number, power = 1) {
    const scene = this.scene;
    const ring = scene.add
      .circle(x, y, 30, tint, 0)
      .setStrokeStyle(2, tint, 0.85)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(-85);
    this.layers.push(ring);
    scene.tweens.add({
      targets: ring,
      radius: 240 * power,
      alpha: { from: 0.85, to: 0 },
      duration: 720,
      ease: 'Cubic.easeOut',
      onComplete: () => ring.destroy(),
    });
  }

  /**
   * Short scanline density burst — gives the world a brief CRT-glitch
   * jolt without ever obscuring gameplay. Fires on alarms.
   */
  scanlineBurst() {
    if (!this.scanlines) return;
    const s = this.scanlines;
    this.scene.tweens.killTweensOf(s);
    s.setAlpha(1);
    this.scene.tweens.add({
      targets: s,
      alpha: { from: 4.5, to: 1 },
      duration: 320,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Tint-flash the whole viewport. `tint` is 0xRRGGBB; `power` (0..1)
   * scales the peak alpha. Used for alarms and critical events — sits
   * at a high depth so it bathes the entire scene briefly.
   */
  flash(tint: number, power = 0.5, durationMs = 420) {
    const { width, height } = this.scene.scale;
    const fl = this.scene.add
      .rectangle(0, 0, width, height, tint, 0)
      .setOrigin(0, 0)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScrollFactor(0)
      .setDepth(220);
    this.scene.tweens.add({
      targets: fl,
      alpha: { from: power, to: 0 },
      duration: durationMs,
      ease: 'Cubic.easeOut',
      onComplete: () => fl.destroy(),
    });
  }

  destroy() {
    for (const l of this.layers) l.destroy();
    this.layers.length = 0;
  }
}
